import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw, Eye, EyeOff } from "lucide-react";
import { encryptAesGcm } from "../services/crypto_aes";
import { fetchAdmins, updateAdmin, addAdmin } from "../services/admins";
import AdminCardActions from "../components/AdminCardActions";
import CollapsibleMobileCard from "../components/CollapsibleMobileCard";
import ToolbarSearch from "../components/ToolbarSearch";
import { SECRET_KEY } from "../config/vars";

const phoneRegex = /^(\+?38)?0\d{9}$/;

const formatPhone = (phone) => {
    let digits = phone.replace(/\D/g, "");
    if (!digits.startsWith("38")) {
        digits = "38" + digits;
    }
    return "+" + digits;
};

const Admins = () => {
    const [admins, setAdmins] = useState([]);
    const [filteredAdmins, setFilteredAdmins] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [edited, setEdited] = useState({});
    const [newAdmin, setNewAdmin] = useState({
        name: "",
        email: "",
        phone: "",
        password_hash: "",
        descriptions: "",
        status: true,
        mfa_enabled: true,
        is_superadmin: false,
    });
    const [showModal, setShowModal] = useState(false);
    const [showNewAdminPassword, setShowNewAdminPassword] = useState(false);
    const [showEditedAdminPassword, setShowEditedAdminPassword] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        loadData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        // Фільтрування адмінів коли змінюється admins або searchQuery
        const query = searchQuery.toLowerCase();
        const filtered = admins.filter(
            (admin) =>
                admin.name.toLowerCase().includes(query) ||
                admin.email.toLowerCase().includes(query) ||
                admin.phone.includes(query)
        );
        setFilteredAdmins(filtered);
    }, [admins, searchQuery]);

    const loadData = async () => {
        try {
            setLoading(true);
            setMessage("");
            const data = await fetchAdmins();
            setAdmins(data.admins);
            setIsSuperAdmin(data.is_superadmin);
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        const sorted = [...filteredAdmins];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const handleEdit = (admin) => {
        setEditingId(admin.id);
        setExpandedId(admin.id);
        setEdited({
            name: admin.name,
            email: admin.email,
            phone: admin.phone,
            password_hash: "",
            descriptions: admin.descriptions,
            status: admin.status,
            mfa_enabled: admin.mfa_enabled,
            is_superadmin: admin.is_superadmin,
        });
        setMessage("");
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEdited({});
        setMessage("");
    };

    const handleSave = async (id) => {
        try {
            const updated = { ...edited };

            if (!SECRET_KEY) {
                setMessage("❌ Не налаштований ключ шифрування (REACT_APP_SECRET_KEY)");
                return;
            }

            if (!phoneRegex.test(updated.phone)) {
                setMessage("❌ Неправильний формат телефону");
                return;
            }

            updated.phone = formatPhone(updated.phone);

            // Якщо пароль не змінений, не відправляємо його
            if (!edited.password_hash.trim()) {
                delete updated.password_hash;
            } else {
                updated.password_hash = await encryptAesGcm(SECRET_KEY, edited.password_hash);
            }

            await updateAdmin(id, updated);
            setMessage("✅ Адміна оновлено успішно");
            await loadData();
            setEditingId(null);
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    const handleAdd = async () => {
        try {
            if (!SECRET_KEY) {
                setMessage("❌ Не налаштований ключ шифрування (REACT_APP_SECRET_KEY)");
                return;
            }

            if (!newAdmin.name.trim() || !newAdmin.email.trim()) {
                setMessage("❌ Ім'я та email не можуть бути порожніми");
                return;
            }

            if (!phoneRegex.test(newAdmin.phone)) {
                setMessage("❌ Невірний формат телефону");
                return;
            }

            if (!newAdmin.password_hash.trim()) {
                setMessage("❌ Пароль не може бути порожнім");
                return;
            }

            const encryptedPassword = await encryptAesGcm(SECRET_KEY, newAdmin.password_hash);
            const payload = {
                ...newAdmin,
                phone: formatPhone(newAdmin.phone),
                password_hash: encryptedPassword,
            };

            await addAdmin(payload);
            setNewAdmin({
                name: "",
                email: "",
                phone: "",
                password_hash: "",
                descriptions: "",
                status: true,
                mfa_enabled: true,
                is_superadmin: false,
            });

            setShowModal(false);
            setMessage("✅ Адміна додано успішно");
            await loadData();
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        }
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    {isSuperAdmin && (
                        <button className="add-btn toolbar-icon-btn" onClick={() => setShowModal(true)} title="Додати" aria-label="Додати">
                            <Plus size={16} />
                        </button>
                    )}
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за ім'ям, email або телефоном..."
                    />
                </div>
            </div>

            {message && <div className="message-banner">{message}</div>}

            {/* Модалка додавання */}
            {showModal && isSuperAdmin && (
                <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3 className="mb-2 font-semibold">Додати адміна</h3>
                        <input
                            type="text"
                            placeholder="Ім'я"
                            value={newAdmin.name}
                            onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                        />
                        <input
                            type="email"
                            placeholder="Email"
                            value={newAdmin.email}
                            onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        />
                        <input
                            type="tel"
                            placeholder="Телефон"
                            value={newAdmin.phone}
                            onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                        />
                        <div className="password-input-wrapper">
                            <input
                                type={showNewAdminPassword ? "text" : "password"}
                                placeholder="Пароль"
                                value={newAdmin.password_hash}
                                onChange={(e) => setNewAdmin({ ...newAdmin, password_hash: e.target.value })}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="toggle-password-btn"
                                onClick={() => setShowNewAdminPassword((v) => !v)}
                                title={showNewAdminPassword ? "Приховати пароль" : "Показати пароль"}
                                aria-label={showNewAdminPassword ? "Приховати пароль" : "Показати пароль"}
                            >
                                {showNewAdminPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder="Опис"
                            value={newAdmin.descriptions}
                            onChange={(e) => setNewAdmin({ ...newAdmin, descriptions: e.target.value })}
                        />
                        <label className="modal-checkbox-row">
                            <input
                                type="checkbox"
                                checked={newAdmin.mfa_enabled}
                                onChange={(e) => setNewAdmin({ ...newAdmin, mfa_enabled: e.target.checked })}
                            />
                            <span>Використовувати MFA</span>
                        </label>
                        <label className="modal-checkbox-row">
                            <input
                                type="checkbox"
                                checked={newAdmin.is_superadmin}
                                onChange={(e) => setNewAdmin({ ...newAdmin, is_superadmin: e.target.checked })}
                            />
                            <span>Суперадмін</span>
                        </label>
                        <select
                            value={newAdmin.status}
                            onChange={(e) =>
                                setNewAdmin({ ...newAdmin, status: e.target.value === "true" })
                            }
                        >
                            <option value="true">Активний</option>
                            <option value="false">Неактивний</option>
                        </select>
                        <div className="flex gap-3 mt-4">
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Адаптивне відображення */}
            {isMobile ? (
                <div className="config-grid">
                    {filteredAdmins.map((admin) => (
                        <CollapsibleMobileCard
                            key={admin.id}
                            expanded={expandedId === admin.id}
                            editing={editingId === admin.id}
                            onToggle={() => setExpandedId((cur) => (cur === admin.id ? null : admin.id))}
                            title={admin.name}
                            meta={<span>{admin.status ? "Активний" : "Неактивний"}</span>}
                            preview={admin.email || admin.phone}
                            actions={(
                                <AdminCardActions
                                    editing={editingId === admin.id}
                                    onEdit={() => handleEdit(admin)}
                                    onSave={() => handleSave(admin.id)}
                                    onCancel={handleCancelEdit}
                                />
                            )}
                        >
                            <div>
                                <span className="collapsible-card__field-label">Ім'я</span>
                                {editingId === admin.id ? (
                                    <input value={edited.name} onChange={(e) => setEdited({ ...edited, name: e.target.value })} />
                                ) : admin.name}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Email</span>
                                {editingId === admin.id ? (
                                    <input value={edited.email} onChange={(e) => setEdited({ ...edited, email: e.target.value })} />
                                ) : admin.email}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Телефон</span>
                                {editingId === admin.id ? (
                                    <input value={edited.phone} onChange={(e) => setEdited({ ...edited, phone: e.target.value })} />
                                ) : admin.phone}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Опис</span>
                                {editingId === admin.id ? (
                                    <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} />
                                ) : (admin.descriptions || "—")}
                            </div>
                            {isSuperAdmin ? (
                                <>
                                    <div>
                                        <span className="collapsible-card__field-label">MFA</span>
                                        {editingId === admin.id ? (
                                            <input type="checkbox" checked={edited.mfa_enabled} onChange={(e) => setEdited({ ...edited, mfa_enabled: e.target.checked })} />
                                        ) : (admin.mfa_enabled ? "✅ Так" : "❌ Ні")}
                                    </div>
                                    <div>
                                        <span className="collapsible-card__field-label">Суперадмін</span>
                                        {editingId === admin.id ? (
                                            <input type="checkbox" checked={edited.is_superadmin} onChange={(e) => setEdited({ ...edited, is_superadmin: e.target.checked })} />
                                        ) : (admin.is_superadmin ? "✅ Так" : "❌ Ні")}
                                    </div>
                                    <div>
                                        <span className="collapsible-card__field-label">Статус</span>
                                        {editingId === admin.id ? (
                                            <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                                <option value="true">Активний</option>
                                                <option value="false">Неактивний</option>
                                            </select>
                                        ) : (admin.status ? "Активний" : "Неактивний")}
                                    </div>
                                </>
                            ) : null}
                        </CollapsibleMobileCard>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                                    Ім'я {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>
                                    Email {sortBy === "email" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Пароль</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("phone")}>
                                    Телефон {sortBy === "phone" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                {isSuperAdmin && (
                                    <>
                                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("mfa_enabled")}>
                                            MFA {sortBy === "mfa_enabled" && (sortDir === "asc" ? "▲" : "▼")}
                                        </th>
                                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("is_superadmin")}>
                                            Суперадмін {sortBy === "is_superadmin" && (sortDir === "asc" ? "▲" : "▼")}
                                        </th>
                                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                            Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                        </th>
                                    </>
                                )}
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((admin) => (
                                <tr key={admin.id}>
                                    <td>
                                        {editingId === admin.id ? (
                                            <input value={edited.name} onChange={(e) => setEdited({ ...edited, name: e.target.value })} />
                                        ) : admin.name}
                                    </td>
                                    <td>
                                        {editingId === admin.id ? (
                                            <input value={edited.email} onChange={(e) => setEdited({ ...edited, email: e.target.value })} />
                                        ) : admin.email}
                                    </td>
                                    <td>{editingId === admin.id ? (
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showEditedAdminPassword ? "text" : "password"}
                                                value={edited.password_hash}
                                                onChange={(e) => setEdited({ ...edited, password_hash: e.target.value })}
                                                placeholder="Залишити порожнім щоб не змінювати"
                                                autoComplete="new-password"
                                            />
                                            <button
                                                type="button"
                                                className="toggle-password-btn"
                                                onClick={() => setShowEditedAdminPassword((v) => !v)}
                                                title={showEditedAdminPassword ? "Приховати пароль" : "Показати пароль"}
                                                aria-label={showEditedAdminPassword ? "Приховати пароль" : "Показати пароль"}
                                            >
                                                {showEditedAdminPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    ) : "•••••••"}</td>
                                    <td>
                                        {editingId === admin.id ? (
                                            <input value={edited.phone} onChange={(e) => setEdited({ ...edited, phone: e.target.value })} />
                                        ) : admin.phone}
                                    </td>
                                    <td>
                                        {editingId === admin.id ? (
                                            <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} />
                                        ) : admin.descriptions}
                                    </td>
                                    {isSuperAdmin && (
                                        <>
                                            <td>{editingId === admin.id ? (
                                                <input type="checkbox" checked={edited.mfa_enabled} onChange={(e) => setEdited({ ...edited, mfa_enabled: e.target.checked })} />
                                            ) : admin.mfa_enabled ? "✅" : "❌"}</td>
                                            <td>{editingId === admin.id ? (
                                                <input type="checkbox" checked={edited.is_superadmin} onChange={(e) => setEdited({ ...edited, is_superadmin: e.target.checked })} />
                                            ) : admin.is_superadmin ? "✅" : "❌"}</td>
                                            <td>{editingId === admin.id ? (
                                                <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                                    <option value="true">Активний</option>
                                                    <option value="false">Неактивний</option>
                                                </select>
                                            ) : admin.status ? "Активний" : "Неактивний"}</td>
                                        </>
                                    )}
                                    <td className="actions">
                                        {editingId === admin.id ? (
                                            <>
                                                <button className="icon-button" onClick={() => handleSave(admin.id)} title="Зберегти">
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button className="icon-button" onClick={handleCancelEdit} title="Скасувати">
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="icon-button" onClick={() => handleEdit(admin)} title="Редагувати">
                                                    <Edit className="icon-btn" />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Admins;
